import { TagKey } from '@cof/tag-catalog-fs-esign-mfe';
import { stratum } from '../../constants/stratum.js';

export const TitleType = {
  PoA: 'Power of Attorney',
  TitleAuth: 'Title Authorization',
  TitleApp: 'Title Application',
  SecurityAgreement: 'Security Agreement'
};

export const DEFAULT_STATE_SELECT = 'Please select state';

export const US_STATES = {
  AL: 'Alabama',
  AK: 'Alaska',
  AR: 'Arkansas',
  AZ: 'Arizona',
  AE: 'Armed Forces',
  AA: 'Armed Forces Americas',
  AP: 'Armed Forces Pacific',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District Of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming'
};

const MICHIGAN_STATE_CODE = 'MI';
const DC_STATE_CODE = 'DC';

export const BORROWER_DL_STATE = 'BorrowerDLState';
export const MICHIGAN_BORROWER_DL_STATE = 'MichiganBorrowerDLState';
export const DC_BORROWER_DL_STATE = 'DCBorrowerDLState';
export const CO_BORROWER_DL_STATE = 'CoBorrowerDLState';
export const BORROWER_DL_NUMBER = 'BorrowerDLNumber';
export const CO_BORROWER_DL_NUMBER = 'CoBorrowerDLNumber';
export const ODOMETER_READING = 'OdometerReading';
export const VEHICLE_COLOR = 'VehicleColor';
export const BORROWER_FIRST_NAME = 'BorrowerFirstName';
export const BORROWER_MIDDLE_NAME = 'BorrowerMiddleName';
export const BORROWER_LAST_NAME = 'BorrowerLastName';
export const BORROWER_PREVIOUS_LAST_NAME = 'BorrowerPreviousLastName';
export const BORROWER_SUFFIX = 'BorrowerSuffix';
export const VEHICLE_PURCHASE_DATE = 'VehiclePurchaseDate';

const onlyLetters = /^[a-zA-Z]*$/;
const onlyLettersAndSpaces = /^[a-zA-Z\s]*$/;
const namePattern = /^[a-zA-ZÀ-ÖØ-öø-ÿ\s-]*$/;

const onlyAlphanumeric = /^[a-zA-Z0-9]+$/;
const onlyNumeric = /^[0-9]+$/;

/**
 * @function getAdditionalInputs
 * @returns {AdditionalInputsType}
 */
export function getAdditionalInputs() {
  return {
    DRIVERS_LICENSE_STATE: {
      name: 'DRIVERS_LICENSE_STATE',
      fieldName: BORROWER_DL_STATE,
      label: "Driver's license state",
      optional: false,
      inputType: 'select',
      options: Object.entries(US_STATES),
      errorMessage: `Please select a valid driver's license state`,
      validate: value =>
        Object.keys(US_STATES).includes(value?.toUpperCase()) &&
        value !== DEFAULT_STATE_SELECT,
      hasError: null,
      value: null
    },
    MICHIGAN_DRIVERS_LICENSE_STATE: {
      name: 'MICHIGAN_DRIVERS_LICENSE_STATE',
      fieldName: MICHIGAN_BORROWER_DL_STATE,
      label: "Driver's license state",
      optional: false,
      inputType: 'select',
      options: Object.entries(US_STATES),
      errorMessage: `Please select a valid driver's license state`,
      validate: value =>
        !!value && value?.toUpperCase() === MICHIGAN_STATE_CODE,
      hasError: false,
      value: null,
      disabled: true,
      helperText:
        "As a Michigan resident, you must have a valid Michigan driver's license to e-sign your Title Authorization form."
    },
    DC_DRIVERS_LICENSE_STATE: {
      name: 'DC_DRIVERS_LICENSE_STATE',
      fieldName: DC_BORROWER_DL_STATE,
      label: "Driver's license state",
      optional: false,
      inputType: 'select',
      options: Object.entries(US_STATES),
      errorMessage: `Please select a valid driver's license state`,
      validate: value => !!value && value?.toUpperCase() === DC_STATE_CODE,
      hasError: false,
      value: null,
      disabled: true,
      helperText:
        "As a District of Columbia resident, you must have a valid District of Columbia driver's license to e-sign your Power of Attorney form."
    },
    CO_BORROWER_DRIVERS_LICENSE_STATE: {
      name: 'CO_BORROWER_DRIVERS_LICENSE_STATE',
      fieldName: CO_BORROWER_DL_STATE,
      label: "Driver's license state",
      optional: false,
      inputType: 'select',
      options: Object.entries(US_STATES),
      errorMessage: `Please select a valid driver's license state`,
      validate: value =>
        Object.keys(US_STATES).includes(value?.toUpperCase()) &&
        value !== DEFAULT_STATE_SELECT,
      hasError: null,
      value: null,
      isCoBorrowerInput: true
    },
    DRIVERS_LICENSE_NUMBER: {
      name: 'DRIVERS_LICENSE_NUMBER',
      fieldName: BORROWER_DL_NUMBER,
      label: "Driver's license number",
      optional: false,
      inputType: 'text',
      errorMessage: `Please enter a valid driver's license number`,
      validate: value =>
        !!value &&
        onlyAlphanumeric.test(value) &&
        value.length >= 7 &&
        value.length <= 30,
      maxLength: 30,
      pattern: onlyAlphanumeric,
      hasError: null,
      value: null
    },
    CO_BORROWER_DRIVERS_LICENSE_NUMBER: {
      name: 'CO_BORROWER_DRIVERS_LICENSE_NUMBER',
      fieldName: CO_BORROWER_DL_NUMBER,
      label: "Driver's license number",
      optional: false,
      inputType: 'text',
      errorMessage: `Please enter a valid driver's license number`,
      validate: value =>
        !!value &&
        onlyAlphanumeric.test(value) &&
        value.length >= 7 &&
        value.length <= 30,
      maxLength: 30,
      pattern: onlyAlphanumeric,
      hasError: null,
      value: null,
      isCoBorrowerInput: true
    },
    ODOMETER_READING: {
      name: 'ODOMETER_READING',
      fieldName: ODOMETER_READING,
      label: 'Estimated odometer reading',
      optional: false,
      inputType: 'text',
      errorMessage: 'Please enter a valid odometer reading',
      validate: value =>
        !!value && onlyNumeric.test(value) && value.length <= 6,
      maxLength: 6,
      pattern: onlyNumeric,
      hasError: null,
      value: null
    },
    VEHICLE_COLOR: {
      name: 'VEHICLE_COLOR',
      fieldName: VEHICLE_COLOR,
      label: 'Vehicle color',
      optional: false,
      inputType: 'text',
      errorMessage: 'Please enter a valid vehicle color',
      validate: value => !!value && onlyLetters.test(value),
      pattern: onlyLetters,
      hasError: null,
      value: null
    },
    LEGAL_FIRST_NAME: {
      name: 'LEGAL_FIRST_NAME',
      fieldName: BORROWER_FIRST_NAME,
      label: 'Legal first name',
      optional: false,
      inputType: 'text',
      errorMessage: 'Please enter your legal first name',
      validate: value =>
        !!value &&
        onlyLetters.test(value) &&
        value.length > 1 &&
        value.length <= 32,
      maxLength: 32,
      pattern: onlyLetters,
      hasError: null,
      value: null
    },
    LEGAL_MIDDLE_NAME: {
      name: 'LEGAL_MIDDLE_NAME',
      fieldName: BORROWER_MIDDLE_NAME,
      label: 'Legal middle name',
      optional: true,
      inputType: 'text',
      validate: value =>
        onlyLetters.test(value) && (value == null || value.length <= 20),
      maxLength: 20,
      pattern: onlyLetters,
      hasError: null,
      value: null,
      helperText: 'If you have a middle name, please include it'
    },
    LEGAL_LAST_NAME: {
      name: 'LEGAL_LAST_NAME',
      fieldName: BORROWER_LAST_NAME,
      label: 'Legal last name',
      optional: false,
      inputType: 'text',
      errorMessage: 'Please enter your legal last name',
      validate: value =>
        !!value &&
        onlyLettersAndSpaces.test(value) &&
        value.length > 0 &&
        value.length <= 32,
      maxLength: 32,
      pattern: onlyLettersAndSpaces,
      hasError: null,
      value: null
    },
    PREVIOUS_LAST_NAME: {
      name: 'PREVIOUS_LAST_NAME',
      fieldName: BORROWER_PREVIOUS_LAST_NAME,
      label: 'Previous last name',
      optional: true,
      inputType: 'text',
      errorMessage: 'Please enter your previous last name',
      validate: value =>
        namePattern.test(value) && (value == null || value.length <= 32),
      maxLength: 32,
      pattern: namePattern,
      hasError: null,
      value: null,
      helperText: 'Please add maiden or any previous last name'
    },
    LEGAL_SUFFIX: {
      name: 'LEGAL_SUFFIX',
      fieldName: BORROWER_SUFFIX,
      label: 'Legal suffix',
      optional: true,
      inputType: 'text',
      validate: value =>
        onlyLetters.test(value) && (value == null || value.length <= 10),
      maxLength: 10,
      pattern: onlyLetters,
      hasError: null,
      value: null,
      helperText: 'If you have a suffix as part of your name, please include it'
    },
    VEHICLE_PURCHASE_DATE: {
      name: 'VEHICLE_PURCHASE_DATE',
      fieldName: VEHICLE_PURCHASE_DATE,
      label: 'Vehicle purchase date',
      optional: false,
      inputType: 'date',
      errorMessage: 'Please enter a valid purchase date',
      validate: value => new Date(value).toString() !== 'Invalid Date',
      hasError: null,
      value: null
    }
  };
}

/**
 *
 * @param {ETitleDetails} config
 * @returns {InputDef[]}
 */
export function getAdditionalInputsForState(config) {
  return (
    config?.additionalInfo
      ?.map(field => {
        const additionalInput = Object.values(getAdditionalInputs()).find(
          input =>
            input.fieldName.toLowerCase() === field.fieldName.toLowerCase()
        );
        if (additionalInput) {
          if (field.defaultValue) {
            additionalInput.value = field.defaultValue;
            additionalInput.hasError = false;
          }
          return additionalInput;
        }
        return null;
      })
      ?.filter(input => !!input) ?? []
  );
}

/**
 * Returns Page header for ETitle
 * @function getHeader
 * @param {string} eTitleType
 * @returns {string}
 */
export function getEtitleHeader(eTitleType, isRefresh = false) {
  if (eTitleType) {
    const formName =
      eTitleType === TitleType.PoA ? `Limited ${eTitleType}` : eTitleType;
    if (isRefresh) {
      return `We are processing your refinance. Next, to update the lender on your title to Capital One, your state requires you to sign a ${formName}.`;
    }
    return `In order to update the lender on your title to Capital One, your state requires us to have you sign a ${formName} form.`;
  }
  return '';
}

/**
 * Publishes Stratum tag for a change in the input fields
 * @param {string} documentType
 * @param {{fieldName: string}} inputData
 */
export function publishTagForInputChange(documentType, inputData) {
  const field = inputData.fieldName;
  if (inputData.hasError) {
    stratum.publishTag(
      TagKey.POA_AND_TITLE_AUTHORIZATION_FIELD_ERROR_MESSAGE_DISPLAYED,
      {
        replacements: {
          field
        }
      }
    );
  } else if (documentType === TitleType.PoA) {
    switch (field) {
      case BORROWER_DL_STATE:
      case MICHIGAN_BORROWER_DL_STATE:
      case DC_BORROWER_DL_STATE:
        stratum.publishTag(TagKey.POA_DRIVERS_LICENSE_STATE_SELECTED);
        break;
      case BORROWER_DL_NUMBER:
        stratum.publishTag(TagKey.POA_DRIVERS_LICENSE_NUMBER_FIELD_EDITED_);
        break;
      case ODOMETER_READING:
        stratum.publishTag(TagKey.POA_ODOMETER_READING_FIELD_EDITED);
        break;
      case VEHICLE_COLOR:
        stratum.publishTag(TagKey.POA_VEHICLE_COLOR_FIELD_EDITED);
        break;
      default:
        break;
    }
  } else if (documentType === TitleType.TitleAuth) {
    switch (field) {
      case BORROWER_DL_STATE:
      case MICHIGAN_BORROWER_DL_STATE:
      case DC_BORROWER_DL_STATE:
        stratum.publishTag(
          TagKey.TITLE_AUTHORIZATION__DRIVERS_LICENSE_STATE_SELECTED
        );
        break;
      case BORROWER_FIRST_NAME:
        stratum.publishTag(
          TagKey.TITLE_AUTHORIZATION__LEGAL_FIRST_NAME_FIELD_EDITED
        );
        break;
      case BORROWER_MIDDLE_NAME:
        stratum.publishTag(
          TagKey.TITLE_AUTHORIZATION__LEGAL_MIDDLE_NAME_FIELD_EDITED
        );
        break;
      case BORROWER_LAST_NAME:
        stratum.publishTag(
          TagKey.TITLE_AUTHORIZATION__LEGAL_LAST_NAME_FIELD_EDITED
        );
        break;
      case BORROWER_SUFFIX:
        stratum.publishTag(
          TagKey.TITLE_AUTHORIZATION__LEGAL_SUFFIX_FIELD_EDITED
        );
        break;
      case BORROWER_DL_NUMBER:
        stratum.publishTag(
          TagKey.TITLE_AUTHORIZATION__DRIVERS_LICENSE_NUMBER_FIELD_EDITED
        );
        break;
      case ODOMETER_READING:
        stratum.publishTag(
          TagKey.TITLE_AUTHORIZATION__ODOMETER_READING_FIELD_EDITED
        );
        break;
      case VEHICLE_PURCHASE_DATE:
        stratum.publishTag(
          TagKey.TITLE_AUTHORIZATION__VEHICLE_PURCHASE_DATE_FIELD_EDITED
        );
        break;
      default:
        break;
    }
  } else if (documentType === TitleType.TitleApp) {
    switch (field) {
      case BORROWER_DL_STATE:
      case MICHIGAN_BORROWER_DL_STATE:
      case DC_BORROWER_DL_STATE:
        stratum.publishTag(
          TagKey.TITLE_APPLICATION_DRIVERS_LICENSE_STATE_SELECTED
        );
        break;
      case BORROWER_DL_NUMBER:
        stratum.publishTag(
          TagKey.TITLE_APPLICATION_DRIVERS_LICENSE_NUMBER_FIELD_EDITED_
        );
        break;
      case CO_BORROWER_DL_STATE:
        stratum.publishTag(
          TagKey.TITLE_APPLICATION_CO_BORROWER_DRIVERS_LICENSE_STATE_SELECTED
        );
        break;
      case CO_BORROWER_DL_NUMBER:
        stratum.publishTag(
          TagKey.TITLE_APPLICATION_CO_BORROWER_DRIVERS_LICENSE_NUMBER_FIELD_EDITED
        );
        break;
      default:
        break;
    }
  }
}
